import React, { useEffect, useMemo, useRef, useState } from 'react';
import 'primeicons/primeicons.css';
import { carteraApi } from './api';
import { money, money0, iniciales, PROD, stageStyle } from './helpers';
import { MensajeModal } from './mensajes';

const TRI = 'linear-gradient(90deg,#204B7B,#32B8DF,#00BA6E)';
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const periodoActual = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const shiftPeriodo = (periodo, delta) => {
  const [y, m] = periodo.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const periodoLabel = (periodo) => {
  const [y, m] = periodo.split('-').map(Number);
  return `${MESES[m - 1]} ${y}`;
};

const Skeleton = () => (
  <>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 22 }}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="co-card" style={{ padding: 20 }}>
          <div className="sk" style={{ height: 12, width: '55%', marginBottom: 16 }} />
          <div className="sk" style={{ height: 26, width: '80%', marginBottom: 10 }} />
          <div className="sk" style={{ height: 10, width: '45%' }} />
        </div>
      ))}
    </div>
    <div className="co-card" style={{ height: 230, padding: 20 }}><div className="sk" style={{ height: '100%', width: '100%' }} /></div>
  </>
);

const Kpi = ({ k }) => (
  <div className="co-card" style={{ position: 'relative', padding: '18px 20px', overflow: 'hidden' }}>
    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: k.stripe }} />
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.07em', maxWidth: 120 }}>{k.label}</div>
      <div style={{ width: 34, height: 34, borderRadius: 11, background: k.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className={'pi ' + k.icon} style={{ color: k.accent, fontSize: 15 }} />
      </div>
    </div>
    <div className="tnum" style={{ fontSize: 28, fontWeight: 800, color: '#0F172A', lineHeight: 1, letterSpacing: '-0.02em', marginTop: 12 }}>{k.value}</div>
    <div style={{ fontSize: 12, color: '#64748B', marginTop: 7 }}>{k.sub}</div>
  </div>
);

export const CarteraCobranzaMes = ({ onOpenCredito }) => {
  const [periodo, setPeriodo] = useState(periodoActual());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reload, setReload] = useState(0);
  const [msg, setMsg] = useState(null); // { tipo, ctx }
  const abortRef = useRef(null);

  useEffect(() => {
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await carteraApi.getCobranzaMes(periodo, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setData(res?.data || null);
      } catch (e) {
        if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.message || 'No se pudo cargar la cobranza del mes.');
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [periodo, reload]);

  const ctxDe = (it) => ({
    nombre: it.cliente, clave: it.clave_contrato, prod: it.producto, tel: it.tel, correo: null,
    veh: 'tu unidad', renta: it.meta, dia: it.dia_de_pago, atraso: it.dias_atraso,
    vencido: it.vencido || it.restante, mora: it.mora || 0,
  });
  const abrirMsg = (it, tipo) => setMsg({ tipo, ctx: ctxDe(it) });

  const kpis = useMemo(() => {
    if (!data) return [];
    const t = data.totales || {};
    const prox = data.proximos || [];
    const venc = data.vencidos || [];
    const proxMonto = prox.reduce((s, x) => s + (x.restante || 0), 0);
    return [
      { label: 'Meta del mes', value: money0(t.meta), sub: `${t.contratos || 0} contratos activos`, accent: '#21588C', stripe: TRI, icon: 'pi-wallet', iconBg: 'rgba(33,88,140,.08)' },
      { label: 'Cobrado / efectividad', value: money0(t.cobrado), sub: `${t.efectividad || 0}% de efectividad`, accent: '#00BA6E', stripe: '#22EC9A', icon: 'pi-check-circle', iconBg: 'rgba(0,186,110,.08)' },
      { label: 'Por vencer (≤10 días)', value: String(prox.length), sub: money0(proxMonto), accent: '#2BA7CE', stripe: '#32B8DF', icon: 'pi-clock', iconBg: 'rgba(43,167,206,.08)' },
      { label: 'Vencido total', value: money0(t.vencido), sub: `${venc.length} contratos`, accent: '#DC2626', stripe: '#DC2626', icon: 'pi-exclamation-circle', iconBg: 'rgba(220,38,38,.08)' },
    ];
  }, [data]);

  const grupos = data?.grupos || [];
  const proximos = data?.proximos || [];
  const vencidos = data?.vencidos || [];
  const isEmpty = !loading && !error && grupos.length === 0 && proximos.length === 0 && vencidos.length === 0;

  return (
    <div className="co-fade">
      {/* Selector de periodo */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <span style={{ fontSize: 11, color: '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em' }}>Periodo</span>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid var(--line)', borderRadius: 11, padding: '4px 6px' }}>
          <button className="btn btn-sm btn-ghost" style={{ padding: '6px 10px' }} onClick={() => setPeriodo((p) => shiftPeriodo(p, -1))}><span className="pi pi-chevron-left" /></button>
          <span className="tnum" style={{ minWidth: 90, textAlign: 'center', fontSize: 13.5, fontWeight: 700, color: '#0F172A', textTransform: 'capitalize' }}>{periodoLabel(periodo)}</span>
          <button className="btn btn-sm btn-ghost" style={{ padding: '6px 10px' }} onClick={() => setPeriodo((p) => shiftPeriodo(p, 1))}><span className="pi pi-chevron-right" /></button>
        </div>
        {periodo !== periodoActual() && (
          <button className="btn btn-sm btn-cyan" onClick={() => setPeriodo(periodoActual())}>Volver a hoy</button>
        )}
      </div>

      {loading && <Skeleton />}

      {error && !loading && (
        <div className="co-card" style={{ padding: '70px 24px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: '#FBE7E7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span className="pi pi-exclamation-triangle" style={{ fontSize: 26, color: '#DC2626' }} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0F172A', marginBottom: 6 }}>No pudimos cargar la información</div>
          <div style={{ fontSize: 13.5, color: '#64748B', marginBottom: 20 }}>{error}</div>
          <button className="btn btn-green" onClick={() => setReload((n) => n + 1)}><span className="pi pi-refresh" />Reintentar</button>
        </div>
      )}

      {!loading && !error && isEmpty && (
        <div className="co-card" style={{ padding: '74px 24px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 20, background: '#F1F6FA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <span className="pi pi-inbox" style={{ fontSize: 26, color: '#94A3B8' }} />
          </div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Sin cobros este mes</div>
          <div style={{ fontSize: 13.5, color: '#64748B' }}>No hay contratos con vencimientos en el periodo seleccionado.</div>
        </div>
      )}

      {!loading && !error && !isEmpty && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
            {kpis.map((k) => <Kpi key={k.label} k={k} />)}
          </div>

          {/* Grupos por día de pago */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, margin: '2px 2px 14px' }}>
            <span style={{ width: 27, height: 27, borderRadius: 8, background: '#E4F5FB', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="pi pi-calendar" style={{ fontSize: 12, color: '#2BA7CE' }} />
            </span>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: '#334155' }}>Contratos por día de pago</span>
            <span style={{ fontWeight: 500, color: '#94A3B8', fontSize: 12.5 }}>· {grupos.length} días activos este mes</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(370px,1fr))', gap: 16, marginBottom: 28 }}>
            {grupos.map((g) => {
              const pctColor = g.pct >= 90 ? '#00BA6E' : (g.pct >= 60 ? '#F0961E' : '#DC2626');
              return (
                <div key={g.dia} className="co-card" style={{ overflow: 'hidden' }}>
                  <div style={{ padding: '17px 17px 13px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>Vencen el {g.dia} de cada mes</div>
                        <div className="tnum" style={{ fontSize: 12, color: '#64748B' }}>{g.contratos} contratos · meta {money0(g.meta)}</div>
                      </div>
                      <div style={{ textAlign: 'center', background: pctColor + '14', padding: '6px 12px', borderRadius: 12 }}>
                        <div className="tnum" style={{ fontSize: 17, fontWeight: 800, color: pctColor, lineHeight: 1 }}>{g.pct}%</div>
                        <div style={{ fontSize: 8.5, color: pctColor, textTransform: 'uppercase', letterSpacing: '.05em', opacity: .85, marginTop: 2 }}>cobrado</div>
                      </div>
                    </div>
                    <div style={{ height: 7, background: '#EEF2F5', borderRadius: 7, marginTop: 13, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${g.pct}%`, background: pctColor, borderRadius: 7, transition: 'width .8s ease' }} />
                    </div>
                    <div style={{ display: 'flex', gap: 10, marginTop: 13 }}>
                      <div style={{ flex: 1, background: '#E5F8EF', borderRadius: 11, padding: '9px 11px' }}>
                        <div style={{ fontSize: 9.5, color: '#00905A', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Cobrado</div>
                        <div className="tnum" style={{ fontSize: 14, fontWeight: 800, color: '#00BA6E', marginTop: 2 }}>{money0(g.cobrado)}</div>
                      </div>
                      <div style={{ flex: 1, background: g.vencido > 0 ? '#FCEADF' : '#F1F5F9', borderRadius: 11, padding: '9px 11px' }}>
                        <div style={{ fontSize: 9.5, color: g.vencido > 0 ? '#B4531A' : '#94A3B8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em' }}>Vencido</div>
                        <div className="tnum" style={{ fontSize: 14, fontWeight: 800, color: g.vencido > 0 ? '#E5631B' : '#94A3B8', marginTop: 2 }}>{g.vencido > 0 ? money0(g.vencido) : '—'}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ maxHeight: 264, overflowY: 'auto', borderTop: '1px solid #F1F5F8' }}>
                    {(g.items || []).map((c) => {
                      const st = stageStyle(c);
                      const conVencido = c.vencido > 0;
                      return (
                        <div key={c.credito_id} onClick={() => onOpenCredito && onOpenCredito(c.credito_id)} className="co-row-hover" style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 15px', borderBottom: '1px solid #F4F7F9', cursor: 'pointer' }}>
                          <div style={{ width: 32, height: 32, borderRadius: 9, background: st.bg, color: st.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>{iniciales(c.cliente)}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.cliente}</div>
                            <div style={{ fontSize: 11, color: '#94A3B8' }}>
                              <span style={{ color: (PROD[c.producto] || PROD.AP).color, fontWeight: 700 }}>{c.producto}</span> · {c.clave_contrato}
                            </div>
                          </div>
                          <div className="tnum" style={{ fontSize: 13, fontWeight: 700, color: conVencido ? '#DC2626' : '#0F172A', textAlign: 'right' }}>{money(conVencido ? c.vencido : c.meta)}</div>
                          <div
                            onClick={(e) => { e.stopPropagation(); abrirMsg(c, conVencido ? 'atraso' : 'recordatorio'); }}
                            title="Recordatorio WhatsApp"
                            className="co-row-hover"
                            style={{ width: 31, height: 31, borderRadius: 9, background: '#E9FBF1', color: '#1FA463', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}
                          >
                            <span className="pi pi-whatsapp" style={{ fontSize: 14 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Por vencer + Vencidos */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div className="co-card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '15px 17px', borderBottom: '1px solid #F1F5F8', display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 27, height: 27, borderRadius: 8, background: '#E4F5FB', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><span className="pi pi-clock" style={{ fontSize: 12, color: '#2BA7CE' }} /></span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>Por vencer</span>
                <span style={{ fontSize: 12, color: '#94A3B8' }}>próximos 10 días</span>
                <span className="tnum" style={{ marginLeft: 'auto', background: '#E4F5FB', color: '#2BA7CE', fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>{proximos.length}</span>
              </div>
              {proximos.length === 0 && <div style={{ padding: '34px 16px', textAlign: 'center', color: '#94A3B8', fontSize: 13 }}>Sin vencimientos próximos</div>}
              {proximos.map((v) => (
                <div key={v.credito_id + '-' + v.fecha_vencimiento} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 17px', borderBottom: '1px solid #F4F7F9' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: 42, height: 42, borderRadius: 12, background: '#EAF6FB', color: '#2BA7CE', flexShrink: 0 }}>
                    <span className="pi pi-calendar" style={{ fontSize: 11, opacity: .85 }} />
                    <span className="tnum" style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.1 }}>{v.dia_de_pago}</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.cliente}</div>
                    <div style={{ fontSize: 11, color: '#94A3B8' }}>{v.clave_contrato} · {v.producto}</div>
                  </div>
                  <div className="tnum" style={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{money(v.restante)}</div>
                  <div onClick={() => abrirMsg(v, 'recordatorio')} className="co-row-hover" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: '#1FA463', background: '#E9FBF1', padding: '7px 11px', borderRadius: 9, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <span className="pi pi-whatsapp" />Recordar
                  </div>
                </div>
              ))}
            </div>

            <div className="co-card" style={{ overflow: 'hidden' }}>
              <div style={{ padding: '15px 17px', borderBottom: '1px solid #F1F5F8', display: 'flex', alignItems: 'center', gap: 9 }}>
                <span style={{ width: 27, height: 27, borderRadius: 8, background: '#FBE7E7', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><span className="pi pi-exclamation-circle" style={{ fontSize: 12, color: '#DC2626' }} /></span>
                <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>Vencidos — cobrar ahora</span>
                <span className="tnum" style={{ marginLeft: 'auto', background: '#FBE7E7', color: '#DC2626', fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 999 }}>{vencidos.length}</span>
              </div>
              {vencidos.length === 0 && (
                <div style={{ padding: '34px 16px', textAlign: 'center', color: '#00BA6E', fontSize: 13, fontWeight: 600 }}>
                  <span className="pi pi-check-circle" style={{ marginRight: 6 }} />Sin vencidos
                </div>
              )}
              {vencidos.map((w) => {
                const st = stageStyle(w);
                return (
                  <div key={w.credito_id + '-' + w.fecha_vencimiento} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 17px', borderBottom: '1px solid #F4F7F9' }}>
                    <div style={{ width: 50, height: 44, borderRadius: 12, background: st.color + '15', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <span className="tnum" style={{ fontSize: 19, fontWeight: 800, color: st.color, lineHeight: 1 }}>{w.dias_atraso}</span>
                      <span style={{ fontSize: 8, color: st.color, textTransform: 'uppercase', letterSpacing: '.04em', opacity: .85, marginTop: 1 }}>días</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{w.cliente}</div>
                      <div className="tnum" style={{ fontSize: 11, color: '#94A3B8' }}>{w.clave_contrato} · vencido <span style={{ color: '#DC2626', fontWeight: 700 }}>{money0(w.vencido)}</span> · mora {money0(w.mora)}</div>
                    </div>
                    <div onClick={() => abrirMsg(w, 'atraso')} className="co-row-hover" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#fff', background: '#E5631B', padding: '8px 12px', borderRadius: 9, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                      <span className="pi pi-send" />Cobrar
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <MensajeModal open={!!msg} tipo={msg?.tipo} ctx={msg?.ctx} onClose={() => setMsg(null)} />
    </div>
  );
};

export default CarteraCobranzaMes;
